import React from 'react';
import { Section } from './ui/Section';
import { motion } from 'framer-motion';
import { ThumbsUpIcon, HeartIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { trackAddToCart } from '../hooks/useMetaPixel';
function Avatar({
  initials,
  color
}: {
  initials: string;
  color: string;
}) {
  return <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${color}`} data-id="element-251">
      {initials}
    </div>;
}
function ReactionBar({
  likes,
  hearts
}: {
  likes: number;
  hearts: number;
}) {
  return <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100" data-id="element-252">
      <div className="flex items-center gap-1" data-id="element-253">
        <span className="flex items-center -space-x-1" data-id="element-254">
          <span className="w-5 h-5 rounded-full bg-[#1877F2] flex items-center justify-center" data-id="element-255">
            <ThumbsUpIcon className="w-3 h-3 text-white" data-id="element-256" />
          </span>
          {hearts > 0 && <span className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center" data-id="element-257">
              <HeartIcon className="w-3 h-3 text-white fill-white" data-id="element-258" />
            </span>}
        </span>
        <span className="text-xs text-gray-500 ml-1.5" data-id="element-259">{likes + hearts}</span>
      </div>
      <div className="flex items-center gap-3 text-xs font-medium text-gray-500" data-id="element-260">
        <span className="cursor-default" data-id="element-261">Me gusta</span>
        <span className="cursor-default" data-id="element-262">Responder</span>
      </div>
    </div>;
}
export function TestimonialsSection() {
  return <Section background="cream" data-id="element-263">
      <h2 className="text-3xl md:text-4xl text-center text-[#2D3B2D] mb-4 font-serif" data-id="element-264">
        Lo que dicen quienes ya empezaron
      </h2>
      <p className="text-center text-sm text-[#4A5D4A] italic mb-12" data-id="element-265">
        Resultados típicos: más claridad, menos ruido mental y un orden que se
        sostiene. (No es magia: es sistema.)
      </p>

      <div className="max-w-2xl mx-auto space-y-4" data-id="element-266">
        {/* Testimonial 1 - María G. */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0,
        duration: 0.5
      }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-267">
          <div className="flex items-start gap-3" data-id="element-268">
            <Avatar initials="MG" color="bg-[#4A7C59]" data-id="element-269" />
            <div className="flex-1 min-w-0" data-id="element-270">
              <div className="flex items-baseline gap-2" data-id="element-271">
                <span className="font-bold text-sm text-[#1C1E21]" data-id="element-272">
                  María G.
                </span>
                <span className="text-xs text-gray-400" data-id="element-273">2 semanas atrás</span>
              </div>
              <p className="text-sm text-[#1C1E21] mt-1.5 leading-relaxed" data-id="element-274">
                Llevaba 3 meses pagando solo mínimos y sintiéndome atrapada. En
                la semana 2 del plan ya tenía claro cuál deuda atacar primero.
                Hoy llevo 2 pagadas. No es magia, es orden.
              </p>
              <ReactionBar likes={24} hearts={8} data-id="element-275" />
            </div>
          </div>
        </motion.div>

        {/* Testimonial 2 - Carolina R. */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1,
        duration: 0.5
      }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-276">
          <div className="flex items-start gap-3" data-id="element-277">
            <Avatar initials="CR" color="bg-[#C4A962]" data-id="element-278" />
            <div className="flex-1 min-w-0" data-id="element-279">
              <div className="flex items-baseline gap-2" data-id="element-280">
                <span className="font-bold text-sm text-[#1C1E21]" data-id="element-281">
                  Carolina R.
                </span>
                <span className="text-xs text-gray-400" data-id="element-282">5 días atrás</span>
              </div>
              <p className="text-sm text-[#1C1E21] mt-1.5 leading-relaxed" data-id="element-283">
                Lo que más me ayudó fue la plantilla de Google Sheets. Ver mis
                números sin drama, sin juicio. Por primera vez no me dio
                ansiedad abrir mi banco.
              </p>
              <ReactionBar likes={31} hearts={12} data-id="element-284" />
            </div>
          </div>
        </motion.div>

        {/* Testimonial 3 - Daniela M. */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2,
        duration: 0.5
      }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-285">
          <div className="flex items-start gap-3" data-id="element-286">
            <Avatar initials="DM" color="bg-[#7B5EA7]" data-id="element-287" />
            <div className="flex-1 min-w-0" data-id="element-288">
              <div className="flex items-baseline gap-2" data-id="element-289">
                <span className="font-bold text-sm text-[#1C1E21]" data-id="element-290">
                  Daniela M.
                </span>
                <span className="text-xs text-gray-400" data-id="element-291">1 semana atrás</span>
              </div>
              <p className="text-sm text-[#1C1E21] mt-1.5 leading-relaxed" data-id="element-292">
                Pensé que era otro PDF motivacional. No. Es un plan real, con
                pasos reales. Mi esposo y yo lo seguimos juntos y ya cancelamos
                la tarjeta que más interés nos cobraba.
              </p>
              <ReactionBar likes={47} hearts={19} data-id="element-293" />
            </div>
          </div>
        </motion.div>

        {/* Testimonial 4 - Valentina P. */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3,
        duration: 0.5
      }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-294">
          <div className="flex items-start gap-3" data-id="element-295">
            <Avatar initials="VP" color="bg-[#D4726A]" data-id="element-296" />
            <div className="flex-1 min-w-0" data-id="element-297">
              <div className="flex items-baseline gap-2" data-id="element-298">
                <span className="font-bold text-sm text-[#1C1E21]" data-id="element-299">
                  Valentina P.
                </span>
                <span className="text-xs text-gray-400" data-id="element-300">3 días atrás</span>
              </div>
              <p className="text-sm text-[#1C1E21] mt-1.5 leading-relaxed" data-id="element-301">
                El protocolo de "mes malo" me salvó. Tuve un gasto inesperado y
                en vez de entrar en pánico, seguí el plan. Eso nunca me había
                pasado.
              </p>
              <ReactionBar likes={18} hearts={6} data-id="element-302" />
            </div>
          </div>
        </motion.div>

        {/* Testimonial 5 - Andrea L. */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.4,
        duration: 0.5
      }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-303">
          <div className="flex items-start gap-3" data-id="element-304">
            <Avatar initials="AL" color="bg-[#3B82A0]" data-id="element-305" />
            <div className="flex-1 min-w-0" data-id="element-306">
              <div className="flex items-baseline gap-2" data-id="element-307">
                <span className="font-bold text-sm text-[#1C1E21]" data-id="element-308">
                  Andrea L.
                </span>
                <span className="text-xs text-gray-400" data-id="element-309">1 día atrás</span>
              </div>
              <p className="text-sm text-[#1C1E21] mt-1.5 leading-relaxed" data-id="element-310">
                Compré por impulso (irónico, lo sé 😅) pero fue la mejor compra
                impulsiva de mi vida. En 30 días pasé de evitar mis números a
                tener un plan que de verdad sigo.
              </p>
              <ReactionBar likes={53} hearts={22} data-id="element-311" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* CTA after testimonials */}
      <div className="text-center mt-12" data-id="element-312">
        <Button icon href="https://momentumdigitalpro.com/cart/44521034743910:1" onClick={trackAddToCart} data-id="element-313">
          Sí, quiero el Plan Antipánico
        </Button>
        <p className="mt-3 text-xs text-[#4A5D4A]/70" data-id="element-314">
          Pago único · Acceso inmediato · Sin suscripción
        </p>
        <p className="mt-4 text-[10px] text-[#4A5D4A]/50 max-w-md mx-auto" data-id="element-315">
          Resultados varían según tus deudas e ingresos. Este es un sistema de
          organización (no asesoría financiera personalizada).
        </p>
      </div>
    </Section>;
}